export type Density = 'compact' | 'comfortable';

// Typography-only density: adds the mobile ramp without widening Density,
// which portal uses for its comfortable/compact user toggle.
export type ThemeDensity = Density | 'mobile';

const comfortableTypography = {
  fontFamily: [
    '-apple-system',
    'BlinkMacSystemFont',
    '"Segoe UI"',
    'Roboto',
    '"Helvetica Neue"',
    'Arial',
    'sans-serif',
    '"Apple Color Emoji"',
    '"Segoe UI Emoji"',
    '"Segoe UI Symbol"',
  ].join(','),
  h1: {
    fontSize: '2.5rem',
    fontWeight: 600,
    lineHeight: 1.2,
  },
  h2: {
    fontSize: '2rem',
    fontWeight: 600,
    lineHeight: 1.3,
  },
  h3: {
    fontSize: '1.75rem',
    fontWeight: 600,
    lineHeight: 1.4,
  },
  h4: {
    fontSize: '1.5rem',
    fontWeight: 600,
    lineHeight: 1.4,
  },
  h5: {
    fontSize: '1.25rem',
    fontWeight: 600,
    lineHeight: 1.5,
  },
  h6: {
    fontSize: '1rem',
    fontWeight: 600,
    lineHeight: 1.6,
  },
  body1: {
    fontSize: '1.125rem',
    lineHeight: 1.5,
  },
  body2: {
    fontSize: '1rem',
    lineHeight: 1.43,
  },
  button: {
    fontSize: '1rem',
    fontWeight: 500,
    textTransform: 'none' as const,
  },
  caption: {
    fontSize: '0.875rem',
    lineHeight: 1.66,
  },
  overline: {
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: 2.66,
    textTransform: 'uppercase' as const,
  },
};

const compactTypography = {
  ...comfortableTypography,
  body1: { fontSize: '1rem', lineHeight: 1.5 },
  body2: { fontSize: '0.875rem', lineHeight: 1.43 },
  button: { fontSize: '0.875rem', fontWeight: 500, textTransform: 'none' as const },
  caption: { fontSize: '0.75rem', lineHeight: 1.66 },
  overline: { fontSize: '0.75rem', fontWeight: 500, lineHeight: 2.66, textTransform: 'uppercase' as const },
};

const mobileTypography = {
  ...comfortableTypography,
  h1: { fontSize: '1.75rem', fontWeight: 600, lineHeight: 1.2 },
  h2: { fontSize: '1.5rem', fontWeight: 600, lineHeight: 1.25 },
  h3: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.3 },
  h4: { fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.4 },
  h5: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.5 },
  h6: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.5 },
  body1: { fontSize: '1rem', lineHeight: 1.5 },
  body2: { fontSize: '0.875rem', lineHeight: 1.43 },
  button: { fontSize: '0.9375rem', fontWeight: 500, textTransform: 'none' as const },
  caption: { fontSize: '0.75rem', lineHeight: 1.33 },
  overline: { fontSize: '0.6875rem', fontWeight: 500, lineHeight: 1.45, textTransform: 'uppercase' as const },
};

export const getTypography = (density: ThemeDensity = 'comfortable') => {
  if (density === 'mobile') {
    return mobileTypography;
  }
  return density === 'compact' ? compactTypography : comfortableTypography;
};

// Keep existing export for backwards compatibility (mobile app doesn't need density)
export const typography = comfortableTypography;